import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  TableSortLabel,
  CircularProgress,
  Alert,
  Snackbar,
  TablePagination,
  Chip,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Extension as ExtensionIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useNavigate } from 'react-router-dom';
import api from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.js';
import Preloader from '../../components/Preloader';


export default function ListagemModulos() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [modulos, setModulos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openView, setOpenView] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [moduloVisualizar, setModuloVisualizar] = useState(null);
  const [moduloParaExcluir, setModuloParaExcluir] = useState(null);

  const [filtros, setFiltros] = useState({
    st_nome: "",
    st_descricao: "",
  });

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("st_nome");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    carregarModulos();
  }, []);

  const carregarModulos = async () => {
    setLoading(true);
    try {
      const response = await api.get('/modulo');
      setModulos(response.data.dados || response.data || []);
    } catch (error) {
      console.error('Erro ao carregar módulos:', error);
      setError('Erro ao carregar módulos');
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (event) => {
    const { name, value } = event.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const visualizarModulo = (modulo) => {
    setModuloVisualizar(modulo);
    setOpenView(true);
  };

  const fecharVisualizacao = () => {
    setOpenView(false);
    setModuloVisualizar(null);
  };

  const confirmarExclusao = (modulo) => {
    if (!modulo) {
      setError("Módulo não encontrado");
      return;
    }
    setModuloParaExcluir(modulo);
    setOpenDeleteConfirm(true);
  };

  const fecharConfirmacaoExclusao = () => {
    setOpenDeleteConfirm(false);
    setModuloParaExcluir(null);
  };

  const deletarModulo = async () => {
    if (!moduloParaExcluir) {
      setError("Nenhum módulo selecionado para exclusão");
      return;
    }

    try {
      setLoading(true);
      const moduloId = moduloParaExcluir.id_modulo || moduloParaExcluir.id;

      if (!moduloId) {
        setError("ID do módulo não encontrado");
        fecharConfirmacaoExclusao();
        return;
      }

      await api.delete(`/modulo/${moduloId}`);

      setSuccess("Módulo excluído com sucesso!");
      fecharConfirmacaoExclusao();
      setTimeout(() => {
        carregarModulos();
      }, 500);
    } catch (error) {
      console.error('Erro ao excluir módulo:', error);

      if (error.response?.status === 401) {
        setError("Sessão expirada. Por favor, faça login novamente.");
      } else if (error.response?.status === 403) {
        setError("Você não tem permissão para excluir módulos.");
      } else if (error.response?.status === 404) {
        setError("Módulo não encontrado.");
      } else {
        const mensagemErro = error.response?.data?.mensagem ||
          error.response?.data?.error ||
          error.message ||
          "Erro ao excluir módulo. Tente novamente.";
        setError(mensagemErro);
      }
      fecharConfirmacaoExclusao();
    } finally {
      setLoading(false);
    }
  };

  // Filtrar e ordenar módulos
  const modulosFiltrados = modulos
    .filter((modulo) => {
      const nomeMatch = !filtros.st_nome ||
        modulo.st_nome?.toLowerCase().includes(filtros.st_nome.toLowerCase());
      const descricaoMatch = !filtros.st_descricao ||
        modulo.st_descricao?.toLowerCase().includes(filtros.st_descricao.toLowerCase());

      return nomeMatch && descricaoMatch;
    })
    .sort((a, b) => {
      let aValue = a[orderBy] || '';
      let bValue = b[orderBy] || '';

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (order === "asc") {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, height: '100vh', overflow: 'auto' }}>
      {/* Snackbars para notificações */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      {/* Box interno com margens de 1,5% */}
      <Box sx={{
        margin: '0 1.5% 1.5% 1.5%',
        height: '90vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: { xs: 2, sm: 3 },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: '1.5rem', sm: '2rem' },
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <ExtensionIcon sx={{
              fontSize: { xs: '1.5rem', sm: '2rem' },
              color: '#20ac9c'
            }} />
            Módulos
          </Typography>
          {(user?.perfil === 'superusuario' || user?.perfil === 'gerente') && (
            <Button
              variant="contained"
              onClick={() => navigate("/modulos/cadastro")}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                minWidth: { xs: 'auto', sm: '200px' },
                backgroundColor: '#20ac9c',
                '&:hover': {
                  backgroundColor: '#167a6f'
                }
              }}
            >
              <AddIcon /> Novo Módulo
            </Button>
          )}
        </Box>

        {/* Container da Tabela */}
        <TableContainer
          component={Paper}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'auto',
            height: {
              xs: 'calc(90vh - 140px)',
              sm: 'calc(90vh - 120px)',
              md: 'calc(90vh - 120px)',
              lg: 'calc(90vh - 120px)'
            },
            maxHeight: {
              xs: 'calc(90vh - 140px)',
              sm: 'calc(90vh - 120px)',
              md: 'calc(90vh - 120px)',
              lg: 'calc(90vh - 120px)'
            },
            width: '100%',
            minWidth: { xs: '100%', sm: '600px' },
            maxWidth: '100%',
            boxShadow: (theme) => theme.palette.mode === 'dark'
              ? '0 2px 8px rgba(0, 0, 0, 0.15)'
              : '0 2px 8px rgba(0, 0, 0, 0.06)',
            borderRadius: 2,
            border: 'none'
          }}
        >
          <Table sx={{ minWidth: '100%', tableLayout: 'fixed' }}>
            <TableHead sx={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              backgroundColor: (theme) => theme.palette.background.paper,
              '& .MuiTableCell-root': {
                backgroundColor: 'inherit',
                borderBottom: '1px solid rgba(224, 224, 224, 1)'
              }
            }}>
              <TableRow>
                <TableCell sx={{
                  width: { xs: '20%', sm: '15%' },
                  minWidth: { xs: '120px', sm: '100px' }
                }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <TableSortLabel
                      active={orderBy === "st_nome"}
                      direction={orderBy === "st_nome" ? order : "asc"}
                      onClick={() => handleRequestSort("st_nome")}
                    >
                      Nome
                    </TableSortLabel>
                    <TextField
                      name="st_nome"
                      size="small"
                      fullWidth
                      value={filtros.st_nome}
                      onChange={handleFiltroChange}
                      placeholder="Filtrar por nome"
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }
                      }}
                    />
                  </Box>
                </TableCell>
                <TableCell sx={{
                  width: { xs: '30%', sm: '25%' },
                  minWidth: { xs: '150px', sm: '120px' }
                }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <TableSortLabel
                      active={orderBy === "st_descricao"}
                      direction={orderBy === "st_descricao" ? order : "asc"}
                      onClick={() => handleRequestSort("st_descricao")}
                    >
                      Descrição
                    </TableSortLabel>
                    <TextField
                      name="st_descricao"
                      size="small"
                      fullWidth
                      value={filtros.st_descricao}
                      onChange={handleFiltroChange}
                      placeholder="Filtrar por descrição"
                      sx={{
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }
                      }}
                    />
                  </Box>
                </TableCell>
                <TableCell sx={{
                  width: { xs: '15%', sm: '12%' },
                  minWidth: { xs: '100px', sm: '80px' }
                }}>
                  <TableSortLabel
                    active={orderBy === "st_icone"}
                    direction={orderBy === "st_icone" ? order : "asc"}
                    onClick={() => handleRequestSort("st_icone")}
                  >
                    Ícone
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{
                  width: { xs: '15%', sm: '15%' },
                  minWidth: { xs: '100px', sm: '120px' },
                  textAlign: 'center'
                }}>
                  Perfis
                </TableCell>


                <TableCell sx={{
                  width: { xs: '10%', sm: '8%' },
                  minWidth: { xs: '80px', sm: '60px' },
                  textAlign: 'center'
                }}>
                  Status
                </TableCell>
                <TableCell sx={{
                  width: { xs: '15%', sm: '12%' },
                  minWidth: { xs: '100px', sm: '80px' },
                  textAlign: 'center'
                }}>
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Preloader message="Carregando módulos..." />
                  </TableCell>
                </TableRow>
              ) : modulosFiltrados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Nenhum módulo encontrado
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                modulosFiltrados
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((modulo, index) => (
                    <TableRow
                      key={modulo.id_modulo || modulo.id || index}
                      sx={{
                        backgroundColor: (theme) =>
                          index % 2 === 0
                            ? theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.02)'
                              : 'rgba(0, 0, 0, 0.02)'
                            : 'transparent',
                        '&:hover': {
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.08)'
                              : 'rgba(0, 0, 0, 0.04)'
                        }
                      }}
                    >
                      <TableCell
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          height: '40px',
                          padding: '8px 16px',
                          verticalAlign: 'middle'
                        }}
                      >
                        {modulo.st_nome}
                      </TableCell>
                      <TableCell
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          height: '40px',
                          padding: '8px 16px',
                          verticalAlign: 'middle'
                        }}
                      >
                        {modulo.st_descricao}
                      </TableCell>
                      <TableCell
                        sx={{
                          height: '40px',
                          padding: '8px 16px',
                          verticalAlign: 'middle'
                        }}
                      >
                        {modulo.st_icone || '-'}
                      </TableCell>


                      <TableCell
                        sx={{
                          height: '40px',
                          padding: '8px 16px',
                          verticalAlign: 'middle',
                          textAlign: 'center'
                        }}
                      >
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                          {(() => {
                            try {
                              const perfis = modulo.st_perfisPermitidos
                                ? JSON.parse(modulo.st_perfisPermitidos)
                                : ['todos'];
                              if (Array.isArray(perfis)) {
                                return perfis.slice(0, 2).map((perfil, idx) => (
                                  <Chip
                                    key={idx}
                                    label={perfil === 'todos' ? 'Todos' : perfil.charAt(0).toUpperCase() + perfil.slice(1)}
                                    size="small"
                                    color="info"
                                  />
                                ));
                              }
                            } catch (e) {
                              return <Chip label="Todos" size="small" color="info" />;
                            }
                            return <Chip label="Todos" size="small" color="info" />;
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          height: '40px',
                          padding: '8px 16px',
                          verticalAlign: 'middle',
                          textAlign: 'center'
                        }}
                      >
                        <Chip
                          label={modulo.bo_ativo ? "Ativo" : "Inativo"}
                          color={modulo.bo_ativo ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          height: '40px',
                          padding: '8px 16px',
                          verticalAlign: 'middle',
                          textAlign: 'center'
                        }}
                      >
                        <Tooltip title="Visualizar">
                          <IconButton size="small" onClick={() => visualizarModulo(modulo)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {(user?.perfil === 'superusuario' || user?.perfil === 'gerente') && (
                          <>
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/modulos/editar/${modulo.id_modulo || modulo.id}`)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Excluir">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  confirmarExclusao(modulo);
                                }}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginação */}
        <TablePagination
          component="div"
          count={modulosFiltrados.length}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Itens por página"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            borderTop: 'none',
            width: '100%',
            marginTop: 0
          }}
        />
      </Box>

      {/* Dialogs */}
      {/* Dialog de Visualização */}
      <Dialog
        open={openView}
        onClose={fecharVisualizacao}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderBottom: 1,
          borderColor: 'divider',
          padding: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          Visualizar Módulo
          <IconButton onClick={fecharVisualizacao}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              label="Nome Técnico"
              value={moduloVisualizar?.st_nome || ""}
              disabled
            />
            <TextField
              fullWidth
              label="Descrição"
              value={moduloVisualizar?.st_descricao || ""}
              disabled
            />
            <TextField
              fullWidth
              label="Ícone"
              value={moduloVisualizar?.st_icone || ""}
              disabled
            />


            <TextField
              fullWidth
              label="Perfis Permitidos"
              value={(() => {
                if (!moduloVisualizar?.st_perfisPermitidos) return "Todos";
                try {
                  const perfis = JSON.parse(moduloVisualizar.st_perfisPermitidos);
                  if (Array.isArray(perfis)) {
                    return perfis.map(p => p === 'todos' ? 'Todos' : p.charAt(0).toUpperCase() + p.slice(1)).join(', ');
                  }
                } catch (e) {
                  return "Todos";
                }
                return "Todos";
              })()}
              disabled
            />
            <TextField
              fullWidth
              label="Status"
              value={moduloVisualizar?.bo_ativo ? "Ativo" : "Inativo"}
              disabled
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderTop: 1,
          borderColor: 'divider',
          padding: 2
        }}>
          <Button onClick={fecharVisualizacao} variant="contained">
            FECHAR
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <Dialog open={openDeleteConfirm} onClose={fecharConfirmacaoExclusao} maxWidth="xs" fullWidth>
        <DialogTitle sx={{
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderBottom: 1,
          borderColor: 'divider',
          padding: 2
        }}>
          Confirmar Exclusão
        </DialogTitle>
        <DialogContent sx={{ padding: 2 }}>
          <Typography>
            Tem certeza que deseja excluir o módulo "{moduloParaExcluir?.st_descricao || moduloParaExcluir?.st_nome}"?
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
          borderTop: 1,
          borderColor: 'divider',
          padding: 2
        }}>
          <Button onClick={fecharConfirmacaoExclusao}>CANCELAR</Button>
          <Button
            variant="contained"
            color="error"
            onClick={deletarModulo}
            disabled={loading}
          >
            EXCLUIR
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
